/**
 * Parse / serialize helpers for the structured Shadow editor.
 *
 * The Design Constructor stores a shadow knob as a raw CSS `box-shadow`
 * string (one or more comma-separated layers). The editor wants to expose it
 * as friendly per-layer fields (inset, x, y, blur, spread, colour) — but the
 * STORAGE FORMAT stays the CSS string, so existing partner themes keep
 * parsing + rendering unchanged. These functions are the bridge:
 *
 *   parseShadow(cssString)        → ShadowLayer[] | null
 *   serializeShadow(ShadowLayer[]) → cssString
 *
 * `parseShadow` returns `null` when the value can't be safely represented as
 * structured layers (unusual units, calc(), etc.) — the editor then falls
 * back to a raw text field so nothing is ever lost.
 */

export interface ShadowLayer {
  /** `inset` keyword present → inner shadow. */
  inset: boolean;
  /** offset-x in px. */
  x: number;
  /** offset-y in px. */
  y: number;
  /** blur radius in px (≥ 0). */
  blur: number;
  /** spread radius in px. */
  spread: number;
  /** CSS colour string (hex / rgb(a) / hsl(a) / keyword). '' = omitted. */
  color: string;
}

/** A sensible starting layer for the "+ Add shadow layer" action. */
export const DEFAULT_LAYER: ShadowLayer = {
  inset: false,
  x: 0,
  y: 2,
  blur: 8,
  spread: 0,
  color: 'rgba(0,0,0,0.2)',
};

/** Split a comma-separated list at the TOP level only — commas inside
 *  parentheses (e.g. `rgba(0,0,0,.5)`) are NOT separators. */
function splitTopLevel(value: string): string[] {
  const parts: string[] = [];
  let depth = 0;
  let cur = '';
  for (const ch of value) {
    if (ch === '(') depth++;
    else if (ch === ')') depth = Math.max(0, depth - 1);
    if (ch === ',' && depth === 0) {
      parts.push(cur);
      cur = '';
    } else {
      cur += ch;
    }
  }
  if (cur.trim() !== '') parts.push(cur);
  return parts;
}

/** Tokenize one layer by whitespace, keeping parenthesised groups
 *  (`rgba(0, 0, 0, .5)`) intact even when they contain spaces. */
function tokenizeLayer(layer: string): string[] {
  const tokens: string[] = [];
  let depth = 0;
  let cur = '';
  for (const ch of layer) {
    if (ch === '(') {
      depth++;
      cur += ch;
    } else if (ch === ')') {
      depth = Math.max(0, depth - 1);
      cur += ch;
    } else if (/\s/.test(ch) && depth === 0) {
      if (cur !== '') {
        tokens.push(cur);
        cur = '';
      }
    } else {
      cur += ch;
    }
  }
  if (cur !== '') tokens.push(cur);
  return tokens;
}

// Bare number or px length (`0`, `-6px`, `.5`, `44px`). Anything with another
// unit (em/rem/%) deliberately fails → that layer becomes unparseable → raw.
const LENGTH_RE = /^-?\d*\.?\d+(px)?$/;

function parseLength(token: string): number | null {
  if (!LENGTH_RE.test(token)) return null;
  const n = parseFloat(token);
  return Number.isNaN(n) ? null : n;
}

function parseLayer(layer: string): ShadowLayer | null {
  const trimmed = layer.trim();
  if (trimmed === '') return null;
  const tokens = tokenizeLayer(trimmed);

  let inset = false;
  const lengths: number[] = [];
  const colorTokens: string[] = [];
  for (const t of tokens) {
    if (t.toLowerCase() === 'inset') {
      inset = true;
      continue;
    }
    const len = parseLength(t);
    if (len !== null) {
      lengths.push(len);
      continue;
    }
    colorTokens.push(t);
  }

  // box-shadow needs offset-x + offset-y; allows up to blur + spread.
  if (lengths.length < 2 || lengths.length > 4) return null;
  // More than one non-length token (or a length with an unsupported unit
  // that fell through to here) means we can't represent it safely.
  if (colorTokens.length > 1) return null;

  const [x = 0, y = 0, blur = 0, spread = 0] = lengths;
  return { inset, x, y, blur, spread, color: colorTokens[0] ?? '' };
}

/**
 * Parse a CSS `box-shadow` value into structured layers.
 *   - `'none'` / `''`  → `[]`  (no shadow)
 *   - parseable value  → `ShadowLayer[]`
 *   - anything we can't safely round-trip → `null` (caller shows raw text)
 */
export function parseShadow(value: string): ShadowLayer[] | null {
  const v = value.trim();
  if (v === '' || v.toLowerCase() === 'none') return [];
  const layers: ShadowLayer[] = [];
  for (const part of splitTopLevel(v)) {
    const layer = parseLayer(part);
    if (!layer) return null;
    layers.push(layer);
  }
  return layers.length > 0 ? layers : null;
}

/** Serialize structured layers back into a CSS `box-shadow` string.
 *  Empty list → `'none'`. */
export function serializeShadow(layers: ShadowLayer[]): string {
  if (layers.length === 0) return 'none';
  return layers
    .map((l) =>
      [
        l.inset ? 'inset' : '',
        `${l.x}px`,
        `${l.y}px`,
        `${l.blur}px`,
        `${l.spread}px`,
        l.color.trim(),
      ]
        .filter((s) => s !== '')
        .join(' '),
    )
    .join(', ');
}
